import {Coins, HelpCircle, Rocket, TrendingUp, Zap} from 'lucide-react'
import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'

const steps = [
  {
    title: 'Connect a wallet',
    body: 'Penguin Pad runs on Robinhood Chain. Anyone with a wallet can launch, buy, sell, or contribute. No KYC and no application queue.',
  },
  {
    title: 'Buy or contribute',
    body: 'On a live curve, buy and sell until it graduates at 4.2 ETH. On a presale, contribute only during the start/end window, within the creator min/max and remaining hard-cap room. Withdraw stays open until close, or after a creator cancel. Opening a raise costs 0.1 ETH. Creators pick a start (at least 20 minutes from now) and end date and can cancel before start, or only with more than 24 hours left.',
  },
  {
    title: 'Watch progress',
    body: 'Price, raised ETH, and tokens left update as activity comes in. At 4.2 ETH net raised, the curve graduates into LP. Hitting the hard cap does not auto-launch. Click Finalize once sold out, or after the window if the soft cap was met, then claim tokens or refund ETH.',
  },
]

const creatorRewardSections = [
  {
    title: 'How the Uniswap v4 creator fee works',
    body: 'After a token bonds (graduates) or a presale is finalized, trading moves to a Uniswap v4 pool on Robinhood Chain. The opening liquidity position is sent to the dead address (0x000\u2026dEaD) and cannot be withdrawn. Fees are not earned by holding that LP NFT. PenguinHook is attached to the pool. On every Uniswap swap it takes 1% (TRADE_FEE_BPS = 100) of the ETH or WETH side of the trade \u2014 buys and sells \u2014 and splits that fee: 75% to the creator wallet and 25% to the live platform fee wallet (penguinProtocol). Both shares are paid in that same swap transaction. The creator does not claim after each trade. If the creator wallet rejects the ETH or WETH transfer, that share accrues on the hook and the creator can pull it later by calling claimCreatorFees(token) from the creator address. The platform share is always paid out on the swap. The 1% rate and 75/25 split are fixed in the contracts.',
  },
  {
    title: 'On the bonding curve (paid at bonding)',
    body: 'While a quick launch is still on the curve, there is no per-trade fee — buys and sells happen on the curve with nothing taken out along the way. At bonding (graduation, when net ETH raised reaches 4.2 ETH), Penguin Pad takes 1% of the total raised ETH in a single batched fee transaction — split 0.75 (75%) to the creator and 0.25 (25%) to the platform. The first five seconds of buys also pay a separate decaying snipe tax that goes entirely to the fee wallet, not the creator. After bonding, curve trades revert and later fees come only from PenguinHook on Uniswap — paid out on every swap.',
  },
  {
    title: 'Presale raise vs. ongoing creator fee',
    body: 'These are separate. On finalize, Penguin Pad takes 3% of ETH raised. Of what remains, the creator can take 0\u201330% (creatorPct). That ETH is paid once, at finalize. The creator receives no tokens. The rest of the ETH seeds locked LP. Unsold hard-cap tokens are burned. After the Uniswap pool is live, the 1% swap fee applies, split 75/25, and is disbursed to the creator wallet on every Uniswap transaction.',
  },
]

const faqs = [
  {
    q: 'What is a bonding curve launch?',
    a: 'Launchpad curves are a constant-product market with a phantom quote reserve, so the first buy is not free. Anyone can buy or sell until the curve graduates.',
  },
  {
    q: 'When does a curve graduate?',
    a: 'When net quote into the curve reaches 4.2 ETH. Curve buy/sell stop, remaining curve tokens plus 200M reserved supply seed LP. Create, buy, sell, and claim settle on Robinhood Chain.',
  },
  {
    q: 'How is a presale different?',
    a: 'Opening a presale costs 0.1 ETH. Creators set token name, ticker, supply, ETH soft/hard caps (hard cap ≤ 4× soft cap), and per-wallet min/max. Tokens per ETH and listing rate are derived from supply ÷ hard cap. Choose Live now (opens in 20 minutes, the factory floor) or Schedule a later start and end. Creators can cancel before start, or only with more than 24 hours left; contributors can still withdraw pledged ETH. Effective max is the smaller of that max and remaining room to the hard cap. Contribute is only open during the start/end window. Withdraw stays open until close, or after a creator cancel. Remaining room below minContribution can only fill the hard cap as an exact leftover. Penguin Pad takes 3% of ETH raised on successful raises. Remaining ETH splits 70–100% to LP and 0–30% to the creator. Hitting the hard cap does not auto-launch. Click Finalize once sold out, or after the window if the soft cap was met, then claim tokens at the sale rate; miss it and every wallet can refund 100%.',
  },
  {
    q: 'What fees apply on the curve?',
    a: 'Launch costs 0.00025 ETH from the connected wallet and never enters reserves. There is no per-trade fee on the curve — buys and sells happen with nothing taken out along the way. At bonding (graduation at 4.2 ETH net raised), Penguin Pad takes 1% of the total raised ETH in a single batched fee transaction, split 75% creator / 25% platform. After bonding, a 1% fee is disbursed on every Uniswap swap via PenguinHook, split the same way. There is no separate claim step in the normal case. Buys in the first 5 seconds also pay a decaying anti-snipe tax. Remaining curve tokens seed LP at bonding.',
  },
]

function HowItWorks() {
  return (
    <Layout>
      <SeoHead
        title="How Penguin Pad Works"
        description="Learn how Penguin Pad bonding curve launches and community presale raises work, including buying, selling, and bonding."
      />

      <section>
        <div className="mx-auto max-w-4xl px-4 pb-2 pt-12 sm:px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Playbook</p>
          <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">How it works</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">
            Penguin Pad has two permissionless tracks on Robinhood Chain: fair-launch bonding curves and community-funded presales. No KYC. Listings are not endorsements — read the Terms.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {steps.map((step, index) => (
            <div key={step.title} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
              <p className="text-xs font-semibold text-lime-400">
                0
                {index + 1}
              </p>
              <h2 className="mt-2 text-sm font-semibold text-white">{step.title}</h2>
              <p className="mt-2 text-sm text-zinc-400">{step.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <div className="rounded-3xl border border-lime-400/20 bg-lime-400/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
              <Rocket className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-lg font-bold text-white">Launchpad</h2>
            <p className="mt-2 text-sm text-zinc-400">
              Tokens launch on a bonding curve with no per-trade fee. Launch fee 0.00025 ETH. Bonds at 4.2 ETH into LP, where Penguin Pad takes 1% of the raised ETH in one batched payout (75/25 creator/platform); after that, 1% Uniswap fees (75/25 creator/platform) are disbursed on every swap.
            </p>
            <Link to="/launchpad" className="mt-4 inline-flex text-sm font-medium text-lime-400 hover:text-lime-300">
              Browse launches →
            </Link>
          </div>
          <div className="rounded-3xl border border-orange-400/20 bg-orange-400/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-orange-400/20 bg-orange-400/10 text-orange-400">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-lg font-bold text-white">Presale</h2>
            <p className="mt-2 text-sm text-zinc-400">
              Opening a raise costs 0.1 ETH. Creators set token name, ticker, supply, ETH caps, and per-wallet min/max. Tokens per ETH and listing rate are derived from supply ÷ hard cap. Live now opens in 20 minutes (factory floor); Schedule a later start if you want. Cancel before start, or only with more than 24 hours left; contributors can still withdraw. Penguin Pad takes 3% of ETH raised. Remaining ETH splits 70–100% LP / 0–30% creator. Hitting the hard cap does not auto-launch. Click Finalize once sold out, or after the window if the soft cap was met, then claim tokens or refund. After listing, 1% Uniswap fees (75/25 creator/platform) are disbursed on every swap.
            </p>
            <Link to="/presale" className="mt-4 inline-flex text-sm font-medium text-orange-400 hover:text-orange-300">
              Browse raises →
            </Link>
          </div>
        </div>

        <div className="mt-12">
          <div className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-lime-400" aria-hidden="true" />
            <h2 className="text-lg font-bold text-white">Creator Rewards</h2>
          </div>
          <p className="mt-3 text-sm text-zinc-400">
            Creator rewards on Penguin Pad are trading fees, not a token allocation. On a quick launch, there is no per-trade fee on the curve itself — instead, 1% of the total raised ETH is taken at bonding (graduation at 4.2 ETH) in a single batched fee transaction, split 75% creator / 25% platform. After bonding — and after a successful presale lists — trading moves to Uniswap v4 with PenguinHook. The pool's own swap fee is set to 0. The hook takes 1% of the ETH/WETH quote leg on every Uniswap transaction and splits it 75% to the creator and 25% to the platform fee wallet. That creator cut is paid in the same swap.
          </p>
          <div className="mt-5 space-y-3">
            {creatorRewardSections.map(item => (
              <div key={item.title} className="rounded-2xl border border-lime-400/20 bg-lime-400/5 p-4 backdrop-blur-md">
                <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-zinc-400">{item.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-400" aria-hidden="true" />
            <h2 className="text-lg font-bold text-white">FAQ</h2>
          </div>
          <div className="mt-5 space-y-3">
            {faqs.map(item => (
              <details key={item.q} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md">
                <summary className="cursor-pointer list-none text-sm font-semibold text-white">
                  {item.q}
                </summary>
                <p className="mt-2 text-sm text-zinc-400">{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start gap-3 rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-lime-400" aria-hidden="true" />
            <p className="text-sm text-zinc-300">Ready to model a trade? Open the curve calculator, or read the Terms first.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/terms"
              className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white hover:border-white/20"
            >
              Terms
            </Link>
            <Link
              to="/launchpad"
              className="rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-300"
            >
              Browse launches
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default HowItWorks
